'use client';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { isSellable, normalizeProductQuantity } from '@eliadmin/core/src/utils/product-utils';

/** One request holds the usual list; a longer one is read page by page. */
const PAGE_SIZE = 100;

/**
 * Orders of „Zoradiť“, by the position of the admin option („Naposledy pridané | Najlacnejšie |
 * Najdrahšie“, `Konto - zoradenie obľúbených`): the API order of the favourites, then by price.
 */
export const FAVORITES_SORTS = ['recent', 'cheapest', 'priciest'] as const;
export type FavoritesSort = (typeof FAVORITES_SORTS)[number];

/** Every favourite of the customer, all pages of the listing (`/store/products?favorite=true`). */
async function fetchAllFavorites({
  locale,
  authToken,
  signal,
}: {
  locale: string;
  authToken: string;
  signal?: AbortSignal;
}) {
  const products: Product[] = [];
  for (let page = 1; ; page++) {
    const response = await fetchProducts({
      locale,
      authToken,
      signal,
      query: { favorite: true, page, results: PAGE_SIZE },
    });
    products.push(...response.data);
    if (page >= (response.meta?.last_page ?? page)) return products;
  }
}

/**
 * The whole list, so the count and „Pridať všetko do košíka“ see every favourite. The core
 * favourite button invalidates `FAVORITE_PRODUCTS`, so a removed heart drops the card.
 */
export function useFavoriteProducts() {
  const locale = useLocaleStore(state => state.currentLocale);
  const authToken = useCustomerStore(state => state.me?.authToken);

  return useQuery({
    queryKey: [CacheKey.FAVORITE_PRODUCTS, 'nt-account', locale, authToken ?? null],
    queryFn: ({ signal }) => fetchAllFavorites({ locale, authToken: authToken!, signal }),
    enabled: !!authToken,
    placeholderData: keepPreviousData,
  });
}

/**
 * The list in the chosen order. The whole list is loaded anyway, so the price orders are local:
 * instant, and independent of whether the favourites endpoint honours `sort`. Prices are the ones
 * the cards show (`getProductPriceDisplay` with the customer's settings); products on request last.
 */
export function sortFavorites(
  products: Product[],
  sort: FavoritesSort,
  options: Parameters<typeof getProductPriceDisplay>[1],
) {
  if (sort === 'recent') return products;
  const price = (product: Product) => {
    const display = getProductPriceDisplay(product, options);
    return display.priceOnRequest ? null : (display.current ?? 0);
  };
  const direction = sort === 'cheapest' ? 1 : -1;
  return [...products].sort((a, b) => {
    const pa = price(a);
    const pb = price(b);
    if (pa === null || pb === null) return Number(pa === null) - Number(pb === null);
    return (pa - pb) * direction;
  });
}

/**
 * Whether a favourite goes to the cart without a choice – the rules of the core cart button: a
 * product with variants needs one picked on its page; ended, affiliate, on-request or out-of-stock
 * products cannot be bought.
 */
export function canAddDirectly(product: Product) {
  if (product.has_variants || product.variants?.length) return false;
  const supplier = [...(product.suppliers ?? [])].sort((a, b) => a.position - b.position)[0];
  return (
    product.status !== 'ended' &&
    !supplier?.is_affiliate &&
    !getProductPriceDisplay(product).priceOnRequest &&
    isSellable(product, normalizeProductQuantity(1, product))
  );
}
